import { Box } from 'mdello-client-ts';

export type BoxContent =
  | BoxData[]
  | InventoryBoxData
  | SurveyResultBoxData
  | PortalSurveyResultBoxData;

export interface BoxResponseData extends Omit<Box, 'data' | 'metadata'> {
  createdAt: Date;
  data: BoxContent;
  fullName?: string | null;
  metadata?: BoxMetadata | null;
  updatedAt: Date;
}

export interface BoxTableData {
  _id: string;
  createdAt: Date;
  isRead: boolean;
  key: string;
  type: string;
  updatedAt: Date;
  user: string;
  userName?: string;
}

export interface BoxData {
  correctAnswer: string;
  probableAnswers: ProbableAnswers;
  questionDescription: string;
  selectedAnswer: string;
}
export interface BoxImage {
  name: string;
  size: number;
  src: string;
}

export interface InventoryBoxData {
  description: string;
  images: BoxImage[];
  name: string;
}

/** Box timestamps are not consistently typed: ISO strings, space-separated strings,
 *  already-localised strings, epoch numbers and Date instances all occur in the wild. */
export type BoxTimestamp = string | number | Date;

export interface SurveyResultBoxData {
  answer: string;
  answerIdx?: number;
  answers?: string[];
  /** Either a timestamp ("2024-10-23 12:01:24.248858") or an already-localised,
   *  unparseable string ("2 მაი, 2024"). */
  date?: BoxTimestamp;
  question: string;
  surveyId?: string;
  /** Human-readable survey name, not a BoxType. */
  type?: string;
}

/** Survey answer as stored by RESIDENCE_PORTAL_SURVEY_RESULTS boxes. Same concept as
 *  SurveyResultBoxData, different field names. */
export interface PortalSurveyResultBoxData {
  answer: string;
  answerIndex?: number;
  answeredAt?: BoxTimestamp;
  locale?: string;
  options?: string[];
  question: string;
  questionId?: string;
  surveyType?: string;
}

export interface SurveyResultView {
  answer: string;
  answeredAt?: BoxTimestamp;
  locale?: string;
  options: string[];
  question: string;
  selectedIndex: number;
  surveyName: string;
}

export interface BoxMetadata {
  fullName?: string;
  image?: string;
  /** ISO timestamp of the first time an admin opened the box detail page. */
  readAt?: string | null;
}

export interface ProbableAnswers {
  a: string;
  b: string;
  c: string;
  d: string;
}

/** Box types whose records hold a single submitted survey answer. The Residents Portal
 *  writes RESIDENCE_PORTAL_SURVEY_RESULTS; it is not part of the client lib BoxType enum. */
const SURVEY_RESULT_BOX_TYPES = ['SURVEY_RESULT', 'RESIDENCE_PORTAL_SURVEY_RESULTS'];

export const isSurveyResultBoxType = (type?: string | null): boolean =>
  !!type && SURVEY_RESULT_BOX_TYPES.includes(type);

/** Read state lives in the free-form `metadata` object — the box schema has no field for it. */
export const isBoxRead = (box?: { metadata?: BoxMetadata | null } | null): boolean =>
  !!box?.metadata?.readAt;

export const isSurveyResult = (
  data?: BoxContent
): data is SurveyResultBoxData | PortalSurveyResultBoxData =>
  !!data && !Array.isArray(data) && 'question' in data && 'answer' in data;

export const isInventoryItem = (data?: BoxContent): data is InventoryBoxData =>
  !!data && !Array.isArray(data) && 'images' in data;

export const toSurveyResultView = (
  data: SurveyResultBoxData | PortalSurveyResultBoxData
): SurveyResultView => {
  const survey = data as Partial<SurveyResultBoxData> & Partial<PortalSurveyResultBoxData>;
  const rawOptions = survey.options ?? survey.answers;
  const options = Array.isArray(rawOptions) ? rawOptions : [];
  const answer = survey.answer ?? '';
  const index = survey.answerIndex ?? survey.answerIdx;

  return {
    answer,
    answeredAt: survey.answeredAt ?? survey.date,
    locale: survey.locale,
    options,
    question: survey.question ?? '',
    selectedIndex: typeof index === 'number' ? index : options.indexOf(answer),
    surveyName: survey.surveyType ?? survey.type ?? ''
  };
};
